import { useEffect, useState } from "react"
import type { RefObject } from "react"

/**
 * A React hook that detects when an element is visible in the viewport using the Intersection Observer API.
 *
 * @param ref - A ref to the element to observe
 * @param rootMargin - The root margin for the intersection observer (default: "0px")
 * @returns True if the element is intersecting with the viewport, false otherwise
 *
 * @example
 * ```tsx
 * function LazyLoadComponent() {
 *   const ref = useRef<HTMLDivElement>(null);
 *   const isOnScreen = useOnScreen(ref);
 *
 *   return (
 *     <div ref={ref}>
 *       {isOnScreen ? <ExpensiveComponent /> : <div>Loading...</div>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useOnScreen(
  ref: RefObject<Element | null>,
  rootMargin: string = "0px"
): boolean {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting)
      },
      {
        rootMargin
      }
    )
    if (node) {
      observer.observe(node)
    }
    return () => {
      if (node) {
        observer.unobserve(node)
      }
    }
  }, [ref, rootMargin])

  return isIntersecting
}
